/**
 * YouTube admin API client — Phase 14 Plan 14-07 Task 1.
 *
 * Thin fetch wrappers around Plan 14-06's `/api/cms/admin/integrations/youtube/*`
 * super-admin endpoints. Auth rides the existing session cookie
 * (credentials: 'include') — the SuperAdminUser extractor on the backend
 * gates all five routes.
 *
 * Error envelope contract from Plan 14-02/14-06 is `{ error, code, details }`.
 * Errors surface as `YouTubeApiError` instances with a `.code` property
 * attached so UI code can pattern-match on:
 *   - YOUTUBE_OAUTH_CLIENT_NOT_CONFIGURED  → ops fix: deploy env vars
 *   - YOUTUBE_RECONNECT                    → operator fix: re-paste refresh token
 *   - YOUTUBE_UPSTREAM                     → Google-side; retry / wait
 *   - INTEGRATION_NOT_CONFIGURED           → no creds row stored
 *   - VALIDATION_FAILED                    → body shape rejected
 *
 * Pattern mirrors `discordApi.ts` (Plan 14-03) and `twitterApi.ts`
 * (Plan 14-05) verbatim — same fetch shape, same envelope decoding.
 */

import { apiRequest } from '@/lib/api-client'

import {
  UpdateVideoMetadataSchema,
  YouTubeConfigSchema,
  YouTubeVideoSchema,
  YouTubeVideosListSchema,
  type UpdateVideoMetadata,
  type YouTubeConfig,
  type YouTubeCredsInput,
  type YouTubeVideo,
  type YouTubeVideosList,
} from './types'

const BASE = '/api/cms/admin/integrations/youtube'
const FETCH_OPTS: RequestInit = { credentials: 'include' }

/**
 * Error class carrying the typed `code` + `details` from the Plan 14-02
 * error envelope. Page handlers `instanceof YouTubeApiError` and branch
 * on `err.code` (e.g. show the reconnect CTA on YOUTUBE_RECONNECT).
 */
export class YouTubeApiError extends Error {
  public readonly code: string
  public readonly status: number
  public readonly details: unknown
  constructor(
    message: string,
    code: string,
    status: number,
    details: unknown = null,
  ) {
    super(message)
    this.name = 'YouTubeApiError'
    this.code = code
    this.status = status
    this.details = details
    // Restore prototype chain so `instanceof` works after transpilation.
    Object.setPrototypeOf(this, YouTubeApiError.prototype)
  }
}

async function throwFromResponse(response: Response): Promise<never> {
  const data: { error?: string; code?: string; details?: unknown } =
    await response.json().catch(() => ({}))
  const message = data.error || `Request failed (${response.status})`
  const code = data.code || `HTTP_${response.status}`
  throw new YouTubeApiError(message, code, response.status, data.details)
}

export interface ListVideosOptions {
  /** Clamped to [1, 50] server-side. Default 10. */
  limit?: number
}

export const youtubeApi = {
  /**
   * GET /api/cms/admin/integrations/youtube/config — returns the masked
   * connection state. `connected: false` + null fields when no row exists.
   * The plaintext refresh token never leaves the server post-PUT — the
   * client only sees `refreshTokenMasked` (last 6 chars).
   */
  async getConfig(): Promise<YouTubeConfig> {
    const res = await apiRequest(`${BASE}/config`, FETCH_OPTS)
    if (!res.ok) await throwFromResponse(res)
    return YouTubeConfigSchema.parse(await res.json())
  },

  /**
   * PUT /api/cms/admin/integrations/youtube/config — saves a new (or
   * replaces existing) refresh token. Server validates by round-tripping
   * a refresh + discover-channel call BEFORE encrypting and persisting
   * (Plan 14-06 validate-before-persist pattern). Typo'd tokens reject
   * here, not at first list-call.
   */
  async putConfig(body: YouTubeCredsInput): Promise<void> {
    const res = await apiRequest(`${BASE}/config`, {
      ...FETCH_OPTS,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * DELETE /api/cms/admin/integrations/youtube/config — hard-delete
   * (no soft-delete column per Plan 14-02 decision). To "undelete" the
   * admin re-PUTs the same refresh token.
   */
  async deleteConfig(): Promise<void> {
    const res = await apiRequest(`${BASE}/config`, {
      ...FETCH_OPTS,
      method: 'DELETE',
    })
    if (!res.ok) await throwFromResponse(res)
  },

  /**
   * GET /api/cms/admin/integrations/youtube/videos — most-recent uploads
   * via the channel's uploads playlist (Plan 14-06 two-step list:
   * playlistItems → videos hydration). Server clamps `limit` to [1, 50];
   * we don't validate client-side beyond a number-or-undefined check.
   */
  async listVideos(opts: ListVideosOptions = {}): Promise<YouTubeVideosList> {
    const url =
      typeof opts.limit === 'number'
        ? `${BASE}/videos?limit=${opts.limit}`
        : `${BASE}/videos`
    const res = await apiRequest(url, FETCH_OPTS)
    if (!res.ok) await throwFromResponse(res)
    return YouTubeVideosListSchema.parse(await res.json())
  },

  /**
   * PATCH /api/cms/admin/integrations/youtube/videos/:id — partial update
   * (server does a get-then-merge against YouTube's videos.update PUT).
   * `viewCount` in the response is 0 (Google's PUT response excludes
   * statistics) — callers re-list to see updated counts.
   */
  async updateVideo(
    id: string,
    patch: UpdateVideoMetadata,
  ): Promise<YouTubeVideo> {
    // Validate the patch shape locally so a typo'd field doesn't reach the
    // wire as an unvalidated payload — server enforces the same schema but
    // the round-trip is wasted.
    const parsed = UpdateVideoMetadataSchema.parse(patch)
    const res = await apiRequest(
      `${BASE}/videos/${encodeURIComponent(id)}`,
      {
        ...FETCH_OPTS,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      },
    )
    if (!res.ok) await throwFromResponse(res)
    return YouTubeVideoSchema.parse(await res.json())
  },
} as const

export type YouTubeApi = typeof youtubeApi
